<template>
  <div>
    <AltOptionButton
      :mainAction="{ text: selectText || $t('servicesystem.common_objects.commonterms.use'), onClick: () => select() }"
      :loading="selectLoading" :alternateActions="alternateSelectActions" :color="'primary darken-1'" :top="top">
    </AltOptionButton>

    <TransferMachinesDialog :externaltrigger="dialogtrigger" :currentCustomerId="customerId" :allMachines="false"
      :saveFunction="bulkTransferCustomer">
    </TransferMachinesDialog>
    <SetProppertyDialog :externaltrigger="dialogtrigger2" :proppertyOptions="proppertyOptions"
      :saveFunction="bulkSetPropperties">
    </SetProppertyDialog>
    <ChangeModelInfoDialog :externaltrigger="dialogtrigger3" :machine="{}" :saveFunction="bulkSetModel">
    </ChangeModelInfoDialog>
    <SetUnitOnMachinesDialog :externaltrigger="dialogtrigger4" :currentCustomerId="customerId"
      :saveFunction="bulkSetUnit">
    </SetUnitOnMachinesDialog>
    <CreateNewMachineDialog ref="createMachineDialog">
    </CreateNewMachineDialog>
  </div>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import TransferMachinesDialog from "@/components/Dialogs/TransferMachinesDialog.vue";
import SetUnitOnMachinesDialog from "@/components/Dialogs/SetUnitOnMachinesDialog.vue";
import SetProppertyDialog from "@/components/Dialogs/SetProppertyDialog.vue";
import ChangeModelInfoDialog from "@/components/Dialogs/ChangeModelInfoDialog.vue";
import CreateNewMachineDialog from "@/components/Dialogs/CreateMachineDialog.vue";
import AltOptionButton from "@/views/pim/articles/AltOptionButton.vue";

export default {
  name: "MachineSelectionOptions",
  props: {
    filter: Object,
    selectionFilter: Object,
    selectMethod: Function,
    clearSelectionMethod: Function,
    refreshListMethod: Function,
    selectText: String,

    hideSetPropperties: Boolean,
    top: {
      type: Boolean,
      default: true,
    },
    openInNewTabFunc: Function,
    customerId: String,
  },

  components: {
    TransferMachinesDialog,
    SetUnitOnMachinesDialog,
    SetProppertyDialog,
    ChangeModelInfoDialog,
    CreateNewMachineDialog,
    AltOptionButton,
  },
  mixins: [clientAPI],

  data() {
    return {
      selectLoading: false,

      dialogtrigger: 1,
      dialogtrigger2: 1,
      dialogtrigger3: 1,
      dialogtrigger4: 1,

      proppertyOptions: {
        Name: { type: 'string', component: 'QTextField', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.name'), maxlength: 50 },
        Facility: { type: 'string', component: 'QTextField', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.facility') },
        BoughtDate: { type: 'date', component: 'QDateField3', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.bought') },
        InstallationDate: { type: 'date', component: 'QDateField3', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.installation_date') },
        WarrantyExpiresDate: { type: 'date', component: 'QDateField3', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.warranty_expires') },
        Note: { type: 'string', component: 'QTextArea', value: null, clearable: true, label: this.$t('servicesystem.common_objects.machine.note') },
      },
    };
  },

  methods: {
    async select() {
      this.selectLoading = true;
      await this.selectMethod(this.SelectionFilterDto)
        .finally(() => {
          this.selectLoading = false;
        })
    },

    async createNew() {
      var result = await this.$refs.createMachineDialog.show({
        title: "Create Machine",
        customerId: this.customerId ?? null,
      });
      if (result) {
        this.refreshListMethod();
      }
    },

    async bulkSetPropperties(updateDto) {
      var dto = {
        SelectionFilter: this.SelectionFilterDto,
        Updates: updateDto,
      };
      await clientAPI.methods.partialBulkUpdateMachines(dto);
      this.clearSelectionMethod();
      this.refreshListMethod();
    },

    async bulkTransferCustomer(oldCustomerId, newCustomerId) {
      var updateDto = {
        CustomerId: newCustomerId,
        UnitId: null //clear unit when transferring customer to prevent customerId mismatch scenarios
      };
      await this.bulkSetPropperties(updateDto);
    },

    async bulkSetUnit(newUnitId) {
      var updateDto = {
        UnitId: newUnitId
      };
      await this.bulkSetPropperties(updateDto);
    },

    async bulkDeactivate() {
      var updateDto = { IsActive: false };
      await this.bulkSetPropperties(updateDto);
    },

    async bulkSetModel(obj) {
      var updateDto = {
        MachineModelId: obj.MachineModel?.Id ?? null,
        SupplierId: obj.Supplier?.Id ?? null,
        AssetTypeId: obj.AssetType?.Id ?? null,
      };
      await this.bulkSetPropperties(updateDto);
    },
  },

  computed: {
    SelectionFilterDto() {
      return { ...this.selectionFilter, InitialListFilter: this.filter };
    },

    inferredActiveState() {
      //console.log("inferredActiveState", this.filter);
      if (!this.filter || this.filter.ActiveState === undefined || this.filter.ActiveState === 0) {
        return true;
      }
      else if (this.filter && this.filter.ActiveState === -1) {
        return false;
      }
      else {
        return undefined;
      }
    },

    unitEnabled() {
      return this.$store.state.GlobalSettings.Features.UnitEnabled;
    },

    alternateSelectActions() {
      var arr = [
        {
          icon: "mdi-swap-horizontal",
          text: this.$t("servicesystem.common_objects.machine.transfer"),
          color: "secondary",
          onClick: () => { this.dialogtrigger++ }
        },
        {
          icon: "$machine_model",
          text: this.$t("servicesystem.common_objects.machine.set_model_info"),
          color: "tertiary",
          onClick: () => { this.dialogtrigger3++ }
        },
      ];

      if (this.unitEnabled && this.customerId) {
        arr.push({
          icon: "$unit",
          text: this.$t("servicesystem.common_objects.unit.set_unit"),
          color: "primary",
          onClick: () => { this.dialogtrigger4++ }
        },);
      }

      if (!this.hideSetPropperties) {
        arr.push({
          icon: "mdi-pencil",
          text: this.$t("servicesystem.common_objects.commonterms.set_propperties"),
          color: "primary",
          onClick: () => { this.dialogtrigger2++ }
        },);
      }

      //not sure what to do when list is mixed active/inactive so just dont show the button in that case
      if (this.inferredActiveState != undefined) {
        arr.unshift({
          icon: this.inferredActiveState ? "mdi-minus-circle" : "mdi-plus-circle",
          text: this.inferredActiveState ? this.$t("servicesystem.common_objects.commonterms.deactivate") : this.$t("servicesystem.common_objects.commonterms.re_activate"),
          color: this.inferredActiveState ? "error" : "success",
          onClick: () => { this.inferredActiveState ? this.bulkDeactivate() : this.bulkActivate() },
        });
      }

      if (this.openInNewTabFunc !== undefined) {
        arr.push({
          icon: "mdi-open-in-new",
          text: this.$t('servicesystem.common_objects.document.open_in_new_tab'),
          color: "primary",
          onClick: () => { this.openInNewTabFunc() },
        });
      }

      return arr;
    },
  },

  watch: {
    customerId: function () {
      console.log('test', this.customerId);
    },
  },
};
</script>